/* eslint-disable react/react-in-jsx-scope */
import React from 'react';
import PropTypes from 'prop-types';
import * as d3 from 'd3';
import * as ss from 'simple-statistics';
import { isNaN, sumBy, isEmpty, map, filter, isEqual, indexOf, sortBy } from 'lodash';
import { Form } from 'react-bootstrap';

import ChartComponent from '../ChartComponent';

import { DEFAULT_SELECTED_ELEMENTS } from '../../../api/Graphics/graphic-operations';

const SMALL_FONT_SIZE = 25;
const BIG_FONT_SIZE = 50;
const CHART_CLASS = 'simple-number';
const MAT_OPERATIONS = {
  AVERAGE_LAYOUT: 'averaged',
  COUNT_LAYOUT: 'count',
  COUNTDIST_LAYOUT: 'countdist',
  MAX_LAYOUT: 'max',
  MEDIAN_LAYOUT: 'median',
  MIN_LAYOUT: 'min',
  MODE_LAYOUT: 'mode',
  DEV_LAYOUT: 'dev',
  SUM_LAYOUT: 'sum',
  properties: {
    AVERAGE_LAYOUT: { layout: 'averaged', title: 'Average' },
    COUNT_LAYOUT: { layout: 'count', title: 'Count' },
    COUNTDIST_LAYOUT: { layout: 'countdist', title: 'Count Dist.' },
    MAX_LAYOUT: { layout: 'max', title: 'Max' },
    MEDIAN_LAYOUT: { layout: 'median', title: 'Median' },
    MIN_LAYOUT: { layout: 'min', title: 'Min' },
    MODE_LAYOUT: { layout: 'mode', title: 'Mode' },
    DEV_LAYOUT: { layout: 'dev', title: 'Std. Dev.' },
    SUM_LAYOUT: { layout: 'sum', title: 'Total' }
  }
};

class SimpleNumber extends ChartComponent {
  id = isEmpty(this.props.atlasID)
    ? `${CHART_CLASS}-card-${this.props.isCard}`
    : this.props.atlasID;

  state = {
    layout: MAT_OPERATIONS.AVERAGE_LAYOUT
  };

  changeLayout = event => this.setState({ layout: event.target.id });

  drawChart = () => {
    const {
      alpha,
      borderAlpha,
      borderColor,
      colorRange,
      dataNumeric,
      height,
      isCard,
      numericName,
      selectedElements,
      weight,
      width
    } = this.props;
    const { layout } = this.state;

    const fontSize = isCard ? SMALL_FONT_SIZE : BIG_FONT_SIZE;

    const svg = d3
      .select(`#svg-${this.id}`)
      .attr('viewBox', [0, 0, width, height - (isCard ? 0 : 48)]);

    const dataNumericAsNumbers = sortBy(
      map(dataNumeric, value => Number(value)),
      element => element
    );

    let number = 0;
    let title = '';
    switch (layout) {
      case MAT_OPERATIONS.AVERAGE_LAYOUT:
        number = ss.mean(dataNumericAsNumbers);
        ({ title } = MAT_OPERATIONS.properties.AVERAGE_LAYOUT);
        break;
      case MAT_OPERATIONS.COUNT_LAYOUT:
        number = dataNumericAsNumbers.length;
        ({ title } = MAT_OPERATIONS.properties.COUNT_LAYOUT);
        break;
      case MAT_OPERATIONS.COUNTDIST_LAYOUT:
        number = filter(dataNumeric, (element, index, array) =>
          isEqual(index, indexOf(array, element))
        ).length;
        ({ title } = MAT_OPERATIONS.properties.COUNTDIST_LAYOUT);
        break;
      case MAT_OPERATIONS.DEV_LAYOUT:
        number = ss.standardDeviation(dataNumericAsNumbers);
        ({ title } = MAT_OPERATIONS.properties.DEV_LAYOUT);
        break;
      case MAT_OPERATIONS.MAX_LAYOUT:
        number = ss.max(dataNumericAsNumbers);
        ({ title } = MAT_OPERATIONS.properties.MAX_LAYOUT);
        break;
      case MAT_OPERATIONS.MEDIAN_LAYOUT:
        number = ss.median(dataNumericAsNumbers);
        ({ title } = MAT_OPERATIONS.properties.MEDIAN_LAYOUT);
        break;
      case MAT_OPERATIONS.MIN_LAYOUT:
        number = ss.min(dataNumericAsNumbers);
        ({ title } = MAT_OPERATIONS.properties.MIN_LAYOUT);
        break;
      case MAT_OPERATIONS.MODE_LAYOUT:
        number = ss.mode(dataNumericAsNumbers);
        ({ title } = MAT_OPERATIONS.properties.MODE_LAYOUT);
        break;
      case MAT_OPERATIONS.SUM_LAYOUT:
        number = sumBy(dataNumericAsNumbers, value => (isNaN(value) ? 0 : value));
        ({ title } = MAT_OPERATIONS.properties.SUM_LAYOUT);
        break;
      default:
        break;
    }

    const showText = selectedElements.Text || isCard;

    const data = [showText ? `${title} ${numericName}` : '', this.formatNumber(number)];
    svg
      .append('g')
      .attr('class', `${this.id}`)
      .selectAll('g')
      .data(data)
      .join('text')
      .attr('class', `${this.id}`)
      .attr('font-size', (d, i) => fontSize * (i + 1))
      .attr('x', 0.5 * width)
      .attr('y', (d, i) => (showText ? i + 1 : i + 0.5) * (0.3 * height + fontSize) - fontSize)
      .attr('fill', colorRange)
      .attr('fill-opacity', alpha)
      .attr('stroke', borderColor)
      .attr('stroke-opacity', borderAlpha)
      .attr('stroke-width', weight)
      .attr('text-anchor', 'middle')
      .text(d => d);
  };

  formatNumber(number) {
    let formattedNumber = number;
    if (number % 1 !== 0) formattedNumber = number.toFixed(2);
    if (number * 1e-6 >= 1) formattedNumber = `${(number * 1e-6).toFixed(2)} M`;
    if (number * 1e-9 >= 1) formattedNumber = `${(number * 1e-9).toFixed(2)} Billion`;
    return formattedNumber;
  }

  render() {
    const { isCard } = this.props;
    const { layout } = this.state;
    const matOpsArray = map(MAT_OPERATIONS.properties, element => element);
    const firstLineOpts = filter(
      matOpsArray,
      (element, index, object) => index < object.length / 2
    );
    const secondLineOpts = filter(
      matOpsArray,
      (element, index, object) => index >= object.length / 2
    );
    const optsLinesArray = [firstLineOpts, secondLineOpts];
    return (
      <>
        {!isCard &&
          map(optsLinesArray, optsLine => (
            <Form.Row className="row-cols-5 m-0">
              {map(optsLine, ({ title, layout: optLayout }) => (
                <Form.Check
                  as={Form.Col}
                  className="m-0"
                  inline
                  type="radio"
                  id={optLayout}
                  label={title}
                  checked={layout === optLayout}
                  onChange={this.changeLayout}
                />
              ))}
            </Form.Row>
          ))}
        <svg id={`svg-${this.id}`} />
      </>
    );
  }
}

SimpleNumber.defaultProps = {
  width: 0,
  height: 0,
  dataNumeric: [1, 1.5, 2, 3, 6, 10, 7, 6.5, 6, 5, 3, 0],
  alpha: 0.9,
  weight: 2,
  borderColor: 'red',
  borderAlpha: 0.9,
  colorRange: 'rgba(51, 102, 0, 1)',
  numericName: 'attribute',
  selectedElements: DEFAULT_SELECTED_ELEMENTS,
  isCard: false
};
SimpleNumber.propTypes = {
  width: PropTypes.number,
  height: PropTypes.number,
  dataNumeric: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  colorRange: PropTypes.string,
  alpha: PropTypes.number,
  weight: PropTypes.number,
  borderColor: PropTypes.string,
  borderAlpha: PropTypes.number,
  numericName: PropTypes.string,
  selectedElements: PropTypes.object,
  isCard: PropTypes.bool
};
export default SimpleNumber;
